import { logger } from './logging'

/**
 * Removes all undefined values.
 *
 * Used to create valid JSON objects for getStaticProps.
 * @see https://github.com/vercel/next.js/discussions/11209
 */
export function omitUndefineds<T>(obj: T): T | null {
  try {
    // Check if the input is not an object or is null, return null immediately
    if (typeof obj !== 'object' || obj === null) {
      return null
    }

    return JSON.parse(JSON.stringify(obj)) as T
  } catch (err) {
    logger().error('Failed to omit undefineds', { obj, err })
    return null
  }
}

type DeepOmitUndefinedAndNulls<T> = T extends null
  ? null
  : T extends Array<infer U>
  ? Array<DeepOmitUndefinedAndNulls<U>>
  : T extends object
  ? {
      [K in keyof T as T[K] extends undefined | null ? never : K]: DeepOmitUndefinedAndNulls<T[K]>
    }
  : T

export function omitUndefinedAndNulls<T, R = DeepOmitUndefinedAndNulls<T>>(obj: T): R | null {
  // nullish: null or undefined
  if (obj == null) {
    return null
  }
  // not object: like string, number, boolean, etc
  if (typeof obj !== 'object') {
    return obj as R
  }
  // array or object
  return JSON.parse(JSON.stringify(obj, (_key, value) => (value === null ? undefined : value))) as R
}
